import {
  ContentViewModel,
  CoreContentData,
  EpisodeContentViewModel,
  ExtendedContentData,
  identity,
  Lazy,
  mapMpxLogoToCarouselBackgroundImageUrl,
  mapMpxLogoToCarouselTitleTreatmentUrl,
  mapMpxMediaToCarouselVideoUrl,
  mapMpxMediaTrailer,
  mapMpxRatingsToAgeRate,
  mapMpxTagsToGenres,
  mapMpxThumbnailToCarouselThumbnail,
  MovieContentViewModel,
  MpxEpisodeContentResponse,
  MpxMovieContentResponse,
  MpxSeasonContentResponse,
  MpxSeries,
  MpxSeriesContentResponse,
  repeatMap,
  SeasonContentViewModel,
  SeriesContentViewModel,
  ShallowPreviewOverlayViewModel,
  SimpleBookmarkItem,
} from 'osn-shared';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';

export abstract class ObservableContent<TContent extends ContentViewModel, TShallow, TDetailed> {
  public readonly coreContentData = new Lazy(() =>
    combineLatest([this.lazyShallow.get(), this.lazyRouterLink.get()]).pipe(
      repeatMap(([shallow, routerLink]) => this.mapToCore(shallow, routerLink))
    )
  );

  public readonly extendedContentData = new Lazy(() =>
    combineLatest([this.lazyDetailed.get(), this.bookmarkStream$, this.coreContentData.get()]).pipe(
      repeatMap(([detailed, bookmark, coreData]) => this.mapToExtended(coreData, detailed, bookmark))
    )
  );

  public readonly shallowOverlayViewModelData = new Lazy(() =>
    this.coreContentData.get().pipe(repeatMap((core) => this.mapCoreToShallowOverlayViewModel(core)))
  );

  protected lazyShallow = new Lazy(() => this.shallowProvider(this.content).pipe(repeatMap(identity())));
  protected lazyDetailed = new Lazy(() => this.detailedProvider(this.content).pipe(repeatMap(identity())));
  protected lazyRouterLink = new Lazy(() => this.routerLinkProvider(this.content).pipe(repeatMap(identity())));

  private readonly bookmarkStream$ = new ReplaySubject<SimpleBookmarkItem>(1);

  public constructor(
    public readonly content: TContent,
    protected readonly shallowProvider: (content: TContent) => Observable<TShallow>,
    protected readonly detailedProvider: (content: TContent) => Observable<TDetailed>,
    protected readonly bookmarkProvider: (content: TContent) => Observable<SimpleBookmarkItem>,
    protected readonly routerLinkProvider: (content: TContent) => Observable<string>
  ) {
    this.updateBookmark();
  }

  protected abstract mapToCore(shallow: TShallow, routerLink: string): CoreContentData;
  protected abstract mapToExtended(
    coreContentData: CoreContentData,
    detailed: TDetailed,
    bookmark: SimpleBookmarkItem
  ): ExtendedContentData;

  private mapCoreToShallowOverlayViewModel(core: CoreContentData): Omit<ShallowPreviewOverlayViewModel, 'imgQuery'> {
    return {
      guid: this.content.guid,
      type: this.content.type,
      title: core.title,
      content: this.content,
    };
  }

  public updateBookmark() {
    this.bookmarkProvider(this.content)
      .pipe(take(1))
      .subscribe((bookmark) => this.bookmarkStream$.next(bookmark));
  }

  public updateRouterLink() {
    this.lazyRouterLink.reload();
    this.coreContentData.reload();
  }
}

export class ObservableMovie extends ObservableContent<MovieContentViewModel, MpxMovieContentResponse, MpxMovieContentResponse> {
  protected mapToCore(shallow: MpxMovieContentResponse, routerLink: string): CoreContentData {
    const thumbnails = mapMpxThumbnailToCarouselThumbnail(shallow.thumbnails);
    return {
      type: shallow.programType,
      backgroundImageUrl: mapMpxLogoToCarouselBackgroundImageUrl(shallow.thumbnails, false),
      titleTreatmentImageUrl: mapMpxLogoToCarouselTitleTreatmentUrl(shallow.thumbnails),
      genres: mapMpxTagsToGenres(shallow.tags),
      description: shallow.descriptionLocalized,
      id: shallow.id,
      guid: shallow.guid,
      title: shallow.titleLocalized,
      portraitImageUrl: thumbnails.portraitUrl,
      landscapeImageUrl: thumbnails.landscapeUrl,
      routerLink,
      hasMedia: mapMpxMediaToCarouselVideoUrl(shallow.media) != null,
      hasTrailer: mapMpxMediaTrailer(shallow.media) != null,
      parentGuid: shallow.guid,
      parentTitle: shallow.titleLocalized,
      parentId: shallow.id,
      seasonNumber: undefined,
      episodeNumber: undefined,
    };
  }

  protected mapToExtended(shallow: CoreContentData, detailed: MpxMovieContentResponse, bookmark: SimpleBookmarkItem): ExtendedContentData {
    return {
      ...shallow,
      bookmark,
      ageRate: mapMpxRatingsToAgeRate(detailed.ratings),
      releaseYear: detailed.year,
      numberOfSeasons: undefined,
      numberOfEpisodes: undefined,
    };
  }
}

export class ObservableSeries extends ObservableContent<SeriesContentViewModel, MpxSeriesContentResponse, MpxSeries> {
  // eslint-disable-next-line sonarjs/no-identical-functions
  protected mapToCore(shallow: MpxSeriesContentResponse, routerLink: string): CoreContentData {
    const thumbnails = mapMpxThumbnailToCarouselThumbnail(shallow.thumbnails);
    return {
      type: shallow.programType,
      backgroundImageUrl: mapMpxLogoToCarouselBackgroundImageUrl(shallow.thumbnails, false),
      titleTreatmentImageUrl: mapMpxLogoToCarouselTitleTreatmentUrl(shallow.thumbnails),
      genres: mapMpxTagsToGenres(shallow.tags),
      description: shallow.descriptionLocalized,
      id: shallow.id,
      guid: shallow.guid,
      title: shallow.titleLocalized,
      portraitImageUrl: thumbnails.portraitUrl,
      landscapeImageUrl: thumbnails.landscapeUrl,
      routerLink,
      hasMedia: mapMpxMediaToCarouselVideoUrl(shallow.media) != null,
      hasTrailer: mapMpxMediaTrailer(shallow.media) != null,
      parentGuid: shallow.guid,
      parentTitle: shallow.titleLocalized,
      parentId: shallow.id,
      episodeNumber: undefined,
      seasonNumber: undefined,
    };
  }

  protected mapToExtended(core: CoreContentData, detailed: MpxSeries, bookmark: SimpleBookmarkItem): ExtendedContentData {
    const firstEpisode = detailed.allEpisodes.first();

    return {
      ...core,
      bookmark,
      ageRate: mapMpxRatingsToAgeRate(detailed.series.ratings),
      numberOfSeasons: detailed.seasons.length,
      releaseYear: detailed.series.year,
      numberOfEpisodes: detailed.allEpisodes.length,
    };
  }
}

export class ObservableSeason extends ObservableContent<SeasonContentViewModel, MpxSeries, MpxSeries> {
  private getContentSeasonFromSeries(series: MpxSeries): MpxSeasonContentResponse {
    return series.seasons.find((it) => it.guid === this.content.guid);
  }

  protected mapToCore(shallow: MpxSeries, routerLink: string): CoreContentData {
    const season = this.getContentSeasonFromSeries(shallow);

    const thumbnails = mapMpxThumbnailToCarouselThumbnail(season.thumbnails);
    return {
      type: season.programType,
      backgroundImageUrl: mapMpxLogoToCarouselBackgroundImageUrl(season.thumbnails, false),
      titleTreatmentImageUrl: mapMpxLogoToCarouselTitleTreatmentUrl(season.thumbnails),
      genres: mapMpxTagsToGenres(shallow.series.tags),
      description: season.descriptionLocalized,
      id: season.id,
      guid: season.guid,
      title: season.titleLocalized,
      portraitImageUrl: thumbnails.portraitUrl,
      landscapeImageUrl: thumbnails.landscapeUrl,
      routerLink,
      hasMedia: true,
      hasTrailer: false,
      parentGuid: season.seriesId.split('/').last(),
      parentTitle: shallow.series.titleLocalized,
      parentId: shallow.series.id,
      seasonNumber: season.tvSeasonNumber,
      episodeNumber: undefined,
    };
  }

  protected mapToExtended(core: CoreContentData, detailed: MpxSeries, bookmark: SimpleBookmarkItem): ExtendedContentData {
    const season = this.getContentSeasonFromSeries(detailed);
    const firstEpisode = detailed.episodesBySeasonNumber[season.tvSeasonNumber].first();

    return {
      ...core,
      bookmark,
      ageRate: mapMpxRatingsToAgeRate(detailed.series.ratings),
      numberOfSeasons: detailed.seasons.length,
      releaseYear: detailed.series.year,
      numberOfEpisodes: detailed.allEpisodes.length,
    };
  }
}

export interface ShallowEpisodeDependencies {
  episode: MpxEpisodeContentResponse;
  series: MpxSeriesContentResponse;
}
export class ObservableEpisode extends ObservableContent<EpisodeContentViewModel, ShallowEpisodeDependencies, MpxSeries> {
  // eslint-disable-next-line sonarjs/no-identical-functions
  protected mapToCore(shallow: ShallowEpisodeDependencies, routerLink: string): CoreContentData {
    const episode = shallow.episode;
    const series = shallow.series;

    // Series thumbnails are used for episodes
    const thumbnails = mapMpxThumbnailToCarouselThumbnail(series.thumbnails);
    return {
      type: episode.programType,
      backgroundImageUrl: mapMpxLogoToCarouselBackgroundImageUrl(series.thumbnails, false),
      titleTreatmentImageUrl: mapMpxLogoToCarouselTitleTreatmentUrl(series.thumbnails),
      genres: mapMpxTagsToGenres(episode.tags),
      description: episode.descriptionLocalized,
      id: episode.id,
      guid: episode.guid,
      title: episode.titleLocalized,
      portraitImageUrl: thumbnails.portraitUrl,
      landscapeImageUrl: thumbnails.landscapeUrl,
      routerLink,
      hasMedia: mapMpxMediaToCarouselVideoUrl(episode.media) != null,
      hasTrailer: mapMpxMediaTrailer(episode.media) != null,
      parentGuid: series.guid,
      parentId: series.id,
      parentTitle: series.titleLocalized,
      episodeNumber: episode.tvSeasonEpisodeNumber,
      seasonNumber: episode.tvSeasonNumber,
    };
  }

  protected mapToExtended(core: CoreContentData, detailed: MpxSeries, bookmark: SimpleBookmarkItem): ExtendedContentData {
    const episode = detailed.allEpisodes.find((it) => it.guid === this.content.guid);

    return {
      ...core,
      bookmark,
      ageRate: mapMpxRatingsToAgeRate(detailed.series.ratings),
      numberOfSeasons: detailed.seasons.length,
      releaseYear: detailed.series.year,
      numberOfEpisodes: detailed.allEpisodes.length,
    };
  }
}
